"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { formatRupiah } from "@/lib/utils";
import { DataPendaftaran } from "@/types";
import { ArrowUpDown } from "lucide-react";

export const columns: ColumnDef<DataPendaftaran>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "tahun",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Tahun
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return <div className="text-center">{row.getValue("tahun")}</div>;
    },
  },
  {
    accessorKey: "periode.jalur_jenis.nama_jalur_jenis",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Jalur
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
  },
  {
    accessorKey: "nomor_pendaftaran",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nomor Pendaftaran
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
  },
  {
    accessorKey: "data_siswa.nama_lengkap",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nama Lengkap
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
  },
  {
    accessorKey: "data_siswa.email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
  },
  {
    accessorKey: "data_siswa.handphone",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nomor Handphone
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
  },
  {
    accessorKey: "is_valid_pendaftaran",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status Validasi Pendaftaran
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div>
          {!row.getValue("is_valid_pendaftaran") ||
          row.getValue("is_valid_pendaftaran") == 0 ? (
            <Badge variant={"secondary"}>Belum Divalidasi</Badge>
          ) : (
            <Badge>Sudah Divalidasi</Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "is_valid_isi_ukt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status Validasi Data Penunjang UKT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div>
          {!row.getValue("is_valid_isi_ukt") ||
          row.getValue("is_valid_isi_ukt") == 0 ? (
            <Badge variant={"secondary"}>Belum Divalidasi</Badge>
          ) : (
            <Badge>Sudah Divalidasi</Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "is_lulus",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status Kelulusan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div>
          {row.getValue("is_lulus") === 0 ? (
            <Badge variant={"secondary"}>Belum Lulus</Badge>
          ) : (
            <Badge variant={"success"}>Lulus</Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "is_daftar_kembali",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Status Daftarkembali
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return (
        <div>
          {row.getValue("is_daftar_kembali") == 0 ? (
            <Badge variant={"secondary"}>Belum Daftarkembali</Badge>
          ) : (
            <Badge>Sudah Daftarkembali</Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "data_penetapan_ukt.ref_nominal_ukt.jenis_ukt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Jenis UKT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const jenisUkt =
        row.original?.data_penetapan_ukt?.ref_nominal_ukt?.jenis_ukt
          ?.nama_jenis_ukt;
      return <div>{jenisUkt}</div>;
    },
  },
  {
    accessorKey: "data_penetapan_ukt.nominal_ukt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nominal UKT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const nominalUkt =
        row.original?.data_penetapan_ukt?.ref_nominal_ukt?.nominal_ukt;
      return <div>{nominalUkt ? formatRupiah(Number(nominalUkt)) : ""}</div>;
    },
  },
  {
    accessorKey: "nim",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          NIM
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
  },
];
